import * as React from 'react';
import type { LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';

export type StatTileTone =
  | 'teal' | 'sky' | 'blue' | 'emerald' | 'amber'
  | 'yellow' | 'orange' | 'rose' | 'red' | 'violet';

interface StatTileProps {
  label: string;
  value: React.ReactNode;
  sub?: string;
  icon: LucideIcon;
  tone?: StatTileTone;
  /** Optional hex override (e.g. dynamic leave-type color). Takes precedence over `tone`. */
  color?: string;
  className?: string;
  /** Optional content rendered below the sub-text (e.g. inline actions). */
  children?: React.ReactNode;
}

const tones: Record<StatTileTone, { tileBg: string; iconBg: string; text: string }> = {
  teal:    { tileBg: 'bg-teal-50/70 dark:bg-teal-900/20',       iconBg: 'bg-teal-100 dark:bg-teal-900/30',       text: 'text-teal-700 dark:text-teal-400' },
  sky:     { tileBg: 'bg-sky-50/70 dark:bg-sky-900/20',         iconBg: 'bg-sky-100 dark:bg-sky-900/30',         text: 'text-sky-700 dark:text-sky-400' },
  blue:    { tileBg: 'bg-blue-50/70 dark:bg-blue-900/20',       iconBg: 'bg-blue-100 dark:bg-blue-900/30',       text: 'text-blue-700 dark:text-blue-400' },
  emerald: { tileBg: 'bg-emerald-50/70 dark:bg-emerald-900/20', iconBg: 'bg-emerald-100 dark:bg-emerald-900/30', text: 'text-emerald-700 dark:text-emerald-400' },
  amber:   { tileBg: 'bg-amber-50/70 dark:bg-amber-900/20',     iconBg: 'bg-amber-100 dark:bg-amber-900/30',     text: 'text-amber-700 dark:text-amber-400' },
  yellow:  { tileBg: 'bg-yellow-50/70 dark:bg-yellow-900/20',   iconBg: 'bg-yellow-100 dark:bg-yellow-900/30',   text: 'text-yellow-700 dark:text-yellow-400' },
  orange:  { tileBg: 'bg-orange-50/70 dark:bg-orange-900/20',   iconBg: 'bg-orange-100 dark:bg-orange-900/30',   text: 'text-orange-700 dark:text-orange-400' },
  rose:    { tileBg: 'bg-rose-50/70 dark:bg-rose-900/20',       iconBg: 'bg-rose-100 dark:bg-rose-900/30',       text: 'text-rose-700 dark:text-rose-400' },
  red:     { tileBg: 'bg-red-50/70 dark:bg-red-900/20',         iconBg: 'bg-red-100 dark:bg-red-900/30',         text: 'text-red-700 dark:text-red-400' },
  violet:  { tileBg: 'bg-violet-50/70 dark:bg-violet-900/20',   iconBg: 'bg-violet-100 dark:bg-violet-900/30',   text: 'text-violet-700 dark:text-violet-400' },
};

/**
 * Compact stat/counter tile: a small colored icon box + uppercase label on top,
 * a bold value, and optional sub-text. The standard tile for dense metric rows
 * (attendance counters, salary hours, leave balances). For headline KPIs use
 * `StatCard` instead.
 */
export function StatTile({ label, value, sub, icon: Icon, tone = 'teal', color, className, children }: StatTileProps) {
  const t = tones[tone];
  return (
    <div className={cn('rounded-xl border border-border/40 p-3', !color && t.tileBg, className)}>
      <div className="mb-2 flex items-center gap-1.5">
        <span
          className={cn('flex h-6 w-6 items-center justify-center rounded-md', !color && t.iconBg)}
          style={color ? { backgroundColor: `${color}1A` } : undefined}
        >
          <Icon className={cn('h-3.5 w-3.5', !color && t.text)} style={color ? { color } : undefined} />
        </span>
        <span className="text-[10px] font-medium uppercase leading-tight tracking-wider text-muted-foreground">
          {label}
        </span>
      </div>
      <p
        className={cn('text-lg font-bold leading-tight', !color && t.text)}
        style={color ? { color } : undefined}
      >
        {value}
      </p>
      {sub && <p className="mt-0.5 text-[10px] text-muted-foreground">{sub}</p>}
      {children}
    </div>
  );
}
